import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useOrg } from "../lib/org";
import {
  Button,
  Card,
  ErrorState,
  Field,
  Money,
  PageHeader,
  Select,
  TextField,
} from "../components/ui";
import { dollarsToCents, today } from "../lib/format";
import type { Account, Customer, Item } from "../lib/models";

interface LineDraft {
  itemId: string;
  description: string;
  qty: string;
  unitPrice: string;
  accountId: string;
}

const blankLine: LineDraft = {
  itemId: "",
  description: "",
  qty: "1",
  unitPrice: "0.00",
  accountId: "",
};

export default function InvoiceForm() {
  const { currentOrg } = useOrg();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const currency = currentOrg?.baseCurrency ?? "USD";

  const { data: customers } = useQuery({
    queryKey: ["customers", currentOrg?.id],
    queryFn: () => api.get<Customer[]>("/customers"),
    enabled: !!currentOrg,
  });
  const { data: items } = useQuery({
    queryKey: ["items", currentOrg?.id],
    queryFn: () => api.get<Item[]>("/items"),
    enabled: !!currentOrg,
  });
  const { data: accounts } = useQuery({
    queryKey: ["accounts", currentOrg?.id],
    queryFn: () => api.get<Account[]>("/accounts"),
    enabled: !!currentOrg,
  });
  const incomeAccounts = (accounts ?? []).filter((a) => a.type === "income");
  const itemById = useMemo(
    () => new Map((items ?? []).map((i) => [i.id, i])),
    [items],
  );

  const [header, setHeader] = useState({
    customerId: "",
    docNumber: "",
    txnDate: today(),
    dueDate: "",
    memo: "",
  });
  const [lines, setLines] = useState<LineDraft[]>([{ ...blankLine }]);

  const subtotal = lines.reduce(
    (s, l) => s + Math.round(Number(l.qty || 0) * dollarsToCents(l.unitPrice)),
    0,
  );

  function updateLine(i: number, patch: Partial<LineDraft>) {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function onItemSelect(i: number, itemId: string) {
    const item = itemById.get(itemId);
    updateLine(i, {
      itemId,
      description: item?.name ?? lines[i].description,
      unitPrice: item ? (item.salesPrice / 100).toFixed(2) : lines[i].unitPrice,
      accountId: item?.incomeAccountId ?? lines[i].accountId,
    });
  }

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>("/invoices", {
        customerId: header.customerId,
        docNumber: header.docNumber || undefined,
        txnDate: header.txnDate,
        dueDate: header.dueDate || undefined,
        memo: header.memo || undefined,
        lines: lines.map((l) => ({
          itemId: l.itemId || undefined,
          description: l.description || undefined,
          qty: Number(l.qty || 0),
          unitPrice: dollarsToCents(l.unitPrice),
          accountId: l.accountId || undefined,
        })),
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      navigate(`/invoices/${res.id}`);
    },
  });

  const valid = header.customerId && lines.length > 0 && subtotal >= 0;

  return (
    <div>
      <PageHeader title="Create invoice" subtitle="Bill a customer for goods or services" />
      <Card className="p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <Field required label="Customer">
            <Select
              value={header.customerId}
              onChange={(e) => setHeader({ ...header, customerId: e.target.value })}
            >
              <option value="">— select —</option>
              {(customers ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Invoice no.">
            <TextField
              placeholder="auto"
              value={header.docNumber}
              onChange={(e) => setHeader({ ...header, docNumber: e.target.value })}
            />
          </Field>
          <Field required label="Date">
            <TextField
              type="date"
              value={header.txnDate}
              onChange={(e) => setHeader({ ...header, txnDate: e.target.value })}
            />
          </Field>
          <Field label="Due date">
            <TextField
              type="date"
              value={header.dueDate}
              onChange={(e) => setHeader({ ...header, dueDate: e.target.value })}
            />
          </Field>
        </div>

        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="th">Product / service</th>
                <th className="th">Description</th>
                <th className="th w-20 text-right">Qty</th>
                <th className="th w-32 text-right">Rate</th>
                <th className="th w-32 text-right">Income acct</th>
                <th className="th w-28 text-right">Amount</th>
                <th className="th w-8" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const amount = Math.round(
                  Number(l.qty || 0) * dollarsToCents(l.unitPrice),
                );
                return (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-2 pr-2">
                      <Select
                        value={l.itemId}
                        onChange={(e) => onItemSelect(i, e.target.value)}
                      >
                        <option value="">— none —</option>
                        {(items ?? []).map((it) => (
                          <option key={it.id} value={it.id}>
                            {it.name}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td className="py-2 pr-2">
                      <TextField
                        value={l.description}
                        onChange={(e) =>
                          updateLine(i, { description: e.target.value })
                        }
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <TextField
                        type="number"
                        className="text-right"
                        value={l.qty}
                        onChange={(e) => updateLine(i, { qty: e.target.value })}
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <TextField
                        type="number"
                        step="0.01"
                        className="text-right"
                        value={l.unitPrice}
                        onChange={(e) =>
                          updateLine(i, { unitPrice: e.target.value })
                        }
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <Select
                        value={l.accountId}
                        onChange={(e) =>
                          updateLine(i, { accountId: e.target.value })
                        }
                      >
                        <option value="">default</option>
                        {incomeAccounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.code}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td className="py-2 pr-2 text-right">
                      <Money cents={amount} currency={currency} />
                    </td>
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        className="text-gray-300 hover:text-red-500"
                        onClick={() =>
                          setLines((ls) => ls.filter((_, idx) => idx !== i))
                        }
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setLines((ls) => [...ls, { ...blankLine }])}
          >
            + Add line
          </Button>
          <div className="text-right">
            <span className="mr-3 text-sm text-gray-500">Total</span>
            <span className="text-xl font-semibold">
              <Money cents={subtotal} currency={currency} />
            </span>
          </div>
        </div>

        <div className="mt-4">
          <Field label="Memo">
            <TextField
              value={header.memo}
              onChange={(e) => setHeader({ ...header, memo: e.target.value })}
            />
          </Field>
        </div>

        {create.error && (
          <div className="mt-4">
            <ErrorState error={create.error} />
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => navigate("/invoices")}>
            Cancel
          </Button>
          <Button
            disabled={!valid || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Saving…" : "Save & post invoice"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
